"use client";
import { EditResourceRequest, IResource } from "@gettactic/api";
import { LoadingSpinner } from "@gettactic/components/src/loadings/LoadingSpinner/LoadingSpinner";
import { cn } from "@gettactic/helpers/src/css/cn";
import {
  hasEnabledDeleteResource,
  isLabelFieldEnabled
} from "@gettactic/helpers/src/featureFlags";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import { useAuthenticated } from "@gettactic/hooks";
import { Trash2 } from "lucide-react";
import React from "react";
import { DeleteResourceModal } from "@/features/reserve/components/DeleteResourceModal";
import { api } from "@/lib/api/api";
import { replaceVarsBulkName } from "@/lib/utils";

type TriState = "yes" | "no" | "mixed";

const deriveTriState = (values: boolean[]): TriState => {
  if (values.every(Boolean)) return "yes";
  if (values.every((v) => !v)) return "no";
  return "mixed";
};

const SectionLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <p className="mb-2 font-semibold text-[11px] text-neutral-400 uppercase tracking-wide">
    {children}
  </p>
);

/** Pattern input for bulk text fields: empty keeps each resource's value. */
const PatternField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  previewCount: number;
}> = ({ label, value, onChange, previewCount }) => {
  const inputId = React.useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="font-medium text-neutral-700 text-xs">
        {label}
      </label>
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Keep existing values"
        className="h-8 w-full rounded-md border border-neutral-200 bg-white px-2.5 text-neutral-800 text-sm placeholder-neutral-400 focus:border-neutral-300 focus:outline-none"
      />
      {value.trim() && (
        <p className="text-[11px] text-neutral-400">
          {`Applies as "${replaceVarsBulkName(value, 0)}" … "${replaceVarsBulkName(value, previewCount - 1)}"`}
        </p>
      )}
    </div>
  );
};

/**
 * Bulk boolean control: Yes/No applies to every resource; "Mixed" shows
 * when the individual values differ. The expandable list underneath shows
 * each resource's value and lets it be flipped one by one, like the
 * legacy bulk editor's "Show all (N)" checkbox list.
 */
const TriStateField: React.FC<{
  label: string;
  help: string;
  values: boolean[];
  resourceNames: string[];
  isDanger?: boolean;
  onChangeAll: (value: boolean) => void;
  onChangeOne: (index: number, value: boolean) => void;
}> = ({
  label,
  help,
  values,
  resourceNames,
  isDanger,
  onChangeAll,
  onChangeOne
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const aggregate = deriveTriState(values);
  const options: TriState[] =
    aggregate === "mixed" ? ["no", "mixed", "yes"] : ["no", "yes"];

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={cn(
              "font-medium text-xs",
              isDanger ? "text-red-700" : "text-neutral-700"
            )}
          >
            {label}
          </p>
          <p className="text-[11px] text-neutral-400">{help}</p>
        </div>
        <div className="flex flex-none rounded-lg bg-neutral-100 p-0.5">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              disabled={option === "mixed"}
              onClick={() =>
                option !== "mixed" && onChangeAll(option === "yes")
              }
              className={cn(
                "rounded-md px-2.5 py-1 font-medium text-[11px] capitalize transition-colors",
                aggregate === option
                  ? cn(
                      "bg-white shadow-sm",
                      isDanger && option === "yes"
                        ? "text-red-600"
                        : "text-neutral-900"
                    )
                  : "text-neutral-400",
                option === "mixed" ? "cursor-default" : "hover:text-neutral-700"
              )}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="mt-1 text-[11px] text-neutral-400 underline hover:text-neutral-600"
      >
        {isExpanded ? "Hide" : `Show all (${values.length})`}
      </button>
      {isExpanded && (
        <div className="mt-1.5 flex flex-col gap-1 rounded-md border border-neutral-200 bg-neutral-50 px-2.5 py-2">
          {values.map((checked, index) => (
            <label
              // biome-ignore lint/suspicious/noArrayIndexKey: aligned with the resources array, which is stable for this mount
              key={index}
              className="flex cursor-pointer items-center gap-2 text-neutral-700 text-xs"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChangeOne(index, e.target.checked)}
                className={cn(
                  "h-3.5 w-3.5 rounded border-neutral-300",
                  isDanger ? "accent-red-600" : "accent-primary"
                )}
              />
              <span className="truncate">{resourceNames[index]}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Bulk resource editing, rebuilt for the map builder's resources panel.
 * Same behavior as the legacy GeneralTabMultiple: naming patterns with
 * `{{number}}` / `{{number:N}}` / `{{date}}` / `{{time}}` variables
 * (empty fields keep each resource's value), and tri-state booleans that
 * either apply to every resource or leave the mixed values untouched.
 */
export const BuilderResourceBulkForm: React.FC<{
  resources: IResource[];
  mutate: () => void;
  onResourcesSaved?: (
    updates: { id: string; name: string; label?: string | null }[]
  ) => void;
}> = ({ resources, mutate, onResourcesSaved }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const toast = useLoadingToast({ loadingMessage: "Applying changes..." });
  const [deleteModal, setDeleteModal] = React.useState<IResource[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);

  const [name, setName] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [description, setDescription] = React.useState("");

  // Per-resource values (aligned with `resources`), initialized from each
  // resource's current state — the header Yes/No sets all of them, the
  // expandable list flips them individually.
  const [boolValues, setBoolValues] = React.useState(() => ({
    is_restricted: resources.map((x) => !x.is_open),
    is_approvable: resources.map((x) => x.is_approvable),
    is_unavailable: resources.map((x) => !x.is_available),
    is_walkup: resources.map((x) => !!x.is_walkup)
  }));
  type BoolField = keyof typeof boolValues;

  // Re-sync from the server after a save/refetch changes the underlying
  // resources: the component stays mounted (its key is the id list), so
  // without this the toggles would keep pre-save state that no longer
  // matches is_open / is_approvable / is_available.
  const boolSignature = resources
    .map(
      (r) =>
        `${r.id}:${r.is_open ? 1 : 0}${r.is_approvable ? 1 : 0}${r.is_available ? 1 : 0}${r.is_dedicated ? 1 : 0}${r.is_walkup ? 1 : 0}`
    )
    .join("|");
  // Depending on boolSignature (not `resources`) is intentional — it
  // stands in for the resources' boolean content.
  React.useEffect(() => {
    setBoolValues({
      is_restricted: resources.map((x) => !x.is_open),
      is_approvable: resources.map((x) => x.is_approvable),
      is_unavailable: resources.map((x) => !x.is_available),
      is_walkup: resources.map((x) => !!x.is_walkup)
    });
  }, [boolSignature]);

  const setAll = (field: BoolField, value: boolean) =>
    setBoolValues((prev) => ({
      ...prev,
      [field]: prev[field].map(() => value)
    }));
  const setOne = (field: BoolField, index: number, value: boolean) =>
    setBoolValues((prev) => ({
      ...prev,
      [field]: prev[field].map((v, i) => (i === index ? value : v))
    }));

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    toast.startLoading();
    let errors = 0;
    const savedUpdates: { id: string; name: string; label?: string | null }[] =
      [];

    for (let i = 0; i < resources.length; i++) {
      const resource = resources[i];
      // Resources arrive in pick order — the first one picked gets the
      // first number.
      const insertIndex = i;
      const request: EditResourceRequest = {
        type: resource.type,
        parent_id: resource.parent_id,
        name:
          name !== "" ? replaceVarsBulkName(name, insertIndex) : resource.name,
        label:
          label !== ""
            ? replaceVarsBulkName(label, insertIndex)
            : resource.label,
        description:
          description !== ""
            ? replaceVarsBulkName(description, insertIndex)
            : resource.description,
        total_slots: resource.total_slots,
        maximum_slots_per_user: resource.maximum_slots_per_user,
        // Dedicated desks keep their booking invariants — bulk toggles
        // must not flip is_open (dedicated requires false) or the other
        // booking flags out from under the dedication.
        is_open: resource.is_dedicated ? false : !boolValues.is_restricted[i],
        is_dedicated: resource.is_dedicated,
        is_approvable: resource.is_dedicated
          ? resource.is_approvable
          : boolValues.is_approvable[i],
        is_available: resource.is_dedicated
          ? resource.is_available
          : !boolValues.is_unavailable[i],
        is_walkup: resource.is_dedicated ? false : boolValues.is_walkup[i],
        is_shareable: resource.is_shareable,
        weight: resource.weight,
        map_url: resource.map_url
      };
      const { ok } = await api.client.organizations.resourcePut(
        resource.id,
        request
      );
      if (!ok) {
        errors++;
        continue;
      }
      savedUpdates.push({
        id: resource.id,
        name: request.name,
        label: request.label
      });
    }

    setIsSaving(false);
    if (savedUpdates.length > 0) onResourcesSaved?.(savedUpdates);
    mutate();
    if (errors === 0) {
      toast.success("Changes saved");
      // The patterns were applied — clear them so a second save doesn't
      // re-stamp names, and let the refetch re-sync the toggles.
      setName("");
      setLabel("");
      setDescription("");
    } else {
      toast.error("Sorry we couldn't save all the changes");
    }
  };

  return (
    <form className="flex h-full flex-col" onSubmit={onSubmit}>
      <DeleteResourceModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pb-4">
        <div>
          <SectionLabel>Details · {resources.length} resources</SectionLabel>
          <div className="flex flex-col gap-3">
            <PatternField
              label="Name"
              value={name}
              onChange={setName}
              previewCount={resources.length}
            />
            {isLabelFieldEnabled(authenticatedUser) && (
              <PatternField
                label="Label"
                value={label}
                onChange={setLabel}
                previewCount={resources.length}
              />
            )}
            <PatternField
              label="Description"
              value={description}
              onChange={setDescription}
              previewCount={resources.length}
            />
            <p className="text-[11px] text-neutral-400">
              {
                "Variables: {{number}}, {{number:10}} to start at 10, {{date}}, {{time}}. Empty fields keep each resource's value."
              }
            </p>
          </div>
        </div>

        <div>
          <SectionLabel>Booking</SectionLabel>
          {resources.some((r) => r.is_dedicated) && (
            <p className="mb-3 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2 text-[11px] text-neutral-700">
              {resources.filter((r) => r.is_dedicated).length} of the selected
              resources are dedicated desks — they keep their booking settings
              and are not affected by these toggles.
            </p>
          )}
          <div className="flex flex-col gap-4">
            <TriStateField
              label="Assigned"
              help="Only assigned teams & users can make reservations."
              values={boolValues.is_restricted}
              resourceNames={resources.map((x) => x.name)}
              onChangeAll={(value) => setAll("is_restricted", value)}
              onChangeOne={(index, value) =>
                setOne("is_restricted", index, value)
              }
            />
            <TriStateField
              label="Approval Required"
              help="Reservations need approval by an admin or team manager."
              values={boolValues.is_approvable}
              resourceNames={resources.map((x) => x.name)}
              onChangeAll={(value) => setAll("is_approvable", value)}
              onChangeOne={(index, value) =>
                setOne("is_approvable", index, value)
              }
            />
            <TriStateField
              label="Make unavailable"
              help="No new reservations can be made."
              values={boolValues.is_unavailable}
              resourceNames={resources.map((x) => x.name)}
              isDanger
              onChangeAll={(value) => setAll("is_unavailable", value)}
              onChangeOne={(index, value) =>
                setOne("is_unavailable", index, value)
              }
            />
            <TriStateField
              label="Walk-up"
              help="Can't be reserved from the app — occupancy is managed externally via the API."
              values={boolValues.is_walkup}
              resourceNames={resources.map((x) => x.name)}
              onChangeAll={(value) => setAll("is_walkup", value)}
              onChangeOne={(index, value) => setOne("is_walkup", index, value)}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-none gap-2 border-neutral-200 border-t pt-3">
        <button
          type="submit"
          disabled={isSaving}
          className="h-9 flex-1 rounded-md bg-primary font-medium text-sm text-white hover:opacity-90 disabled:opacity-60"
        >
          {isSaving ? (
            <LoadingSpinner color="text-white" />
          ) : (
            `Save ${resources.length} resources`
          )}
        </button>
        {hasEnabledDeleteResource(authenticatedUser) && (
          <button
            type="button"
            disabled={isSaving}
            onClick={() => setDeleteModal(resources)}
            className="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50"
            title="Delete resources"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </form>
  );
};
